/**
 * Halloween mug designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenMugDesignsPack: ToilePack = {
    slug: 'halloween-mug-designs',
    category: 'season',
    name: 'Halloween mug designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Brew slogan', subject: 'The words "WITCH\'S BREW" lettered in a rounded spooky script with a small cauldron, isolated'},
        {key: 'cat', label: 'Black cat', subject: "A black cat with a witch's hat and green eyes, isolated on a plain ground, no text"},
        {key: 'band', label: 'Pattern band', subject: 'A horizontal band of small pumpkins and bats sized to wrap a mug, isolated, no text'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['for a coffee shop', 'as a matching pair', 'in a single ink color'],
    directionHint: 'try: as a matching pair of mugs',
    seo: {
        title: 'Halloween Mug Designs: Brew, Cat, Pattern Band',
        metaDescription: "Nine halloween mug designs -- witch's brew slogan, black cat, pattern band -- in spooky cute, vintage, and bold graphic finishes.",
        h1: "Halloween mug design: witch's brew slogan, black cat, and pattern band",
        tagline: 'Nine wraps, three mug designs, three finishes each.',
        intro: "Nine flat halloween mug designs: a 'Witch's Brew' slogan with a small cauldron, a black cat with a witch's hat, and a pattern band of pumpkins and bats sized to wrap a mug. Three finishes -- spooky cute, vintage, and bold graphic -- with no mug mockup in frame. Ask for a coffee shop version instead, or a single ink color, and the same three finishes carry it.",
        useCases: [
            'a sublimation mug print',
            'a print-on-demand mug shop',
            'a coffee shop seasonal mug',
            'a matching mug pair',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine flat art designs -- a brew slogan, a black cat, and a wrap-band pattern -- each in spooky cute, vintage, and bold graphic finishes, with no mug in the image.'},
            {q: 'Is there a mug mockup included?', a: 'No -- every design is flat, isolated art meant to wrap onto your own mug template or sublimation blank.'},
            {q: 'What size mug does the pattern band fit?', a: 'The band is sized for a standard 11oz mug wrap; resize it slightly for a 15oz mug or a travel tumbler.'},
            {q: 'Can I sell mugs printed with these designs?', a: 'Yes -- the designs are yours to print and sell, including on a print-on-demand storefront.'},
        ],
    },
}
